import { Fragment, ReactNode, useEffect, useState } from "react";
import { DateRange } from "react-day-picker";
import { AlertCircle, ArrowDownUp, CalendarPlus, ChevronDown, ChevronRight, CircleDot, LucideIcon, PhoneCall, Repeat, RefreshCw, RotateCcw, X } from "lucide-react";
import { denseTable, Section, StateBlock } from "@/components/layout-2/ds";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { cn } from "@/lib/utils";
import { DataService } from "@/services/data";
import { formatParis, parisCalendarDay, parisDayEnd, parisDayStart } from "@/utils/dialer-datetime";
import { formatDecimal, formatNumber, formatPercent } from "@/utils/format-fr";

type CardBreakdownMetrics = {
    quantity: number;
    percentage: number;
    priority_average: number | null;
    priority_minimum: number | null;
    priority_maximum: number | null;
    attempts_average: number | null;
    attempts_minimum: number | null;
    attempts_maximum: number | null;
};

type CardBreakdownGroup = CardBreakdownMetrics & {
    status: string;
    label: string;
    dispositions: Array<CardBreakdownMetrics & { name: string }>;
};

/** What `CampaignCardsTab` serializes. Declared here: it is a tab payload shape. */
export type CampaignCardsContent = {
    total: number;
    groups: CardBreakdownGroup[];
};

/**
 * Bounds sent to the cards endpoint, named as `CARD_FILTER_LOOKUPS` names them. Every
 * figure of the breakdown, the total included, is recomputed on the narrowed set, so a
 * bound is a query param and never a filter applied to the rows already on screen.
 */
type CampaignCardFilters = {
    created_after?: string;
    created_before?: string;
    last_call_after?: string;
    last_call_before?: string;
    priority_min?: string;
    priority_max?: string;
    attempts_min?: string;
    attempts_max?: string;
    /** CSV of engine `Card.state` values. Disposition grouping is a different axis. */
    state?: string;
};

/** Engine states the chip can send. Keys are `CardState` values on the card. */
const ACTIVE_STATES = ["queued", "awaiting_provider", "awaiting_event", "paused"] as const;
const STATE_OPTIONS: Array<{ id: string; label: string; states: readonly string[] }> = [
    { id: "in_flight", label: "In progress", states: ACTIVE_STATES },
    { id: "done", label: "Done", states: ["done"] },
    { id: "cancelled", label: "Cancelled", states: ["cancelled"] },
    { id: "excluded", label: "Excluded", states: ["excluded"] },
    { id: "expired", label: "Expired", states: ["expired"] },
    { id: "failed", label: "Failed", states: ["failed"] }
];

function parseStateFilter(value: string | undefined): string[] {
    return value ? value.split(",").filter(Boolean) : [];
}

function selectedOptionIds(states: string[]): string[] {
    const set = new Set(states);
    return STATE_OPTIONS.filter(option => option.states.every(state => set.has(state))).map(option => option.id);
}

function statesFromOptionIds(ids: string[]): string[] {
    const chosen = new Set(ids);
    return STATE_OPTIONS.filter(option => chosen.has(option.id)).flatMap(option => [...option.states]);
}

function stateSummary(value: string | undefined): string | null {
    const selected = selectedOptionIds(parseStateFilter(value));
    if (selected.length === 0) return null;
    return selected.map(id => STATE_OPTIONS.find(option => option.id === id)?.label).join(", ");
}

/** Dense table, same look as `ObjectListTableResizable layout="dense"`: gray header, compact rows. */
function CardBreakdownCells({ row }: { row: CardBreakdownMetrics }) {
    return (
        <>
            <TableCell className={cn(denseTable.cell, "text-right tabular-nums")}>{formatNumber(row.quantity)}</TableCell>
            <TableCell className={cn(denseTable.cell, "text-right tabular-nums")}>{formatPercent(row.percentage, 2, 2)}</TableCell>
            <TableCell className={cn(denseTable.cell, "text-right tabular-nums")}>{formatDecimal(row.priority_average, 2)}</TableCell>
            <TableCell className={cn(denseTable.cell, "text-right tabular-nums")}>{formatNumber(row.priority_minimum)}</TableCell>
            <TableCell className={cn(denseTable.cell, "text-right tabular-nums")}>{formatNumber(row.priority_maximum)}</TableCell>
            <TableCell className={cn(denseTable.cell, "text-right tabular-nums")}>{formatDecimal(row.attempts_average, 2)}</TableCell>
            <TableCell className={cn(denseTable.cell, "text-right tabular-nums")}>{formatNumber(row.attempts_minimum)}</TableCell>
            <TableCell className={cn(denseTable.cell, "text-right tabular-nums")}>{formatNumber(row.attempts_maximum)}</TableCell>
        </>
    );
}

/**
 * A filter as one compact pill: label, current bounds, a popover to set them and a cross
 * to drop them. Kept local to this panel — the generic list filters of `layout-2` are a
 * DRQ panel over a record list, and this tab serves aggregates from a bespoke endpoint.
 */
function FilterChip({
    label,
    icon: Icon,
    summary,
    disabled,
    onClear,
    onOpenChange,
    children
}: {
    label: string;
    icon: LucideIcon;
    summary: string | null;
    disabled?: boolean;
    onClear: () => void;
    onOpenChange?: (open: boolean) => void;
    children: (close: () => void) => ReactNode;
}) {
    const [open, setOpen] = useState(false);
    const setChipOpen = (next: boolean) => {
        setOpen(next);
        onOpenChange?.(next);
    };

    return (
        <div className={cn("flex h-7 items-center gap-1 rounded-sm border bg-white px-2 text-xs shadow-sm", summary && "bg-gray-100")}>
            <Popover open={open} onOpenChange={setChipOpen}>
                <PopoverTrigger asChild>
                    <button type="button" disabled={disabled} className="flex items-center gap-1.5 whitespace-nowrap disabled:opacity-50" aria-label={`${label} filter`}>
                        <Icon className="h-4 w-4 text-gray-500" />
                        <span className="text-gray-700">{label}</span>
                        {summary && <span className="tabular-nums text-black">{summary}</span>}
                        <ChevronDown className="h-4 w-4 text-gray-500" />
                    </button>
                </PopoverTrigger>
                <PopoverContent align="start" className="w-auto p-3">
                    {children(() => setChipOpen(false))}
                </PopoverContent>
            </Popover>
            {summary && (
                <button type="button" className="rounded-sm p-0.5 hover:bg-gray-200" aria-label={`Clear ${label} filter`} onClick={onClear} disabled={disabled}>
                    <X className="h-3 w-3" />
                </button>
            )}
        </div>
    );
}

type Bounds = { from?: string; to?: string };

function DayRangeChip({ label, icon, hint, value, disabled, onChange }: { label: string; icon: LucideIcon; hint: string; value: Bounds; disabled?: boolean; onChange: (next: Bounds) => void }) {
    // The partial selection of a range being picked: one click gives a start, and the
    // bounds are only worth a request once the second click closes the range.
    const [pending, setPending] = useState<DateRange | undefined>(undefined);
    const selected = pending ?? (value.from ? { from: parisCalendarDay(value.from), to: parisCalendarDay(value.to) } : undefined);
    const summary = value.from || value.to ? `${formatParis(value.from, "dd/MM/yy")} → ${formatParis(value.to, "dd/MM/yy")}` : null;

    return (
        <FilterChip
            label={label}
            icon={icon}
            summary={summary}
            disabled={disabled}
            onClear={() => onChange({})}
            onOpenChange={next => {
                if (!next) setPending(undefined);
            }}
        >
            {close => (
                <div className="space-y-2">
                    <p className="text-xs text-gray-500">{hint}</p>
                    <Calendar
                        mode="range"
                        enableWeekend
                        numberOfMonths={2}
                        selected={selected}
                        defaultMonth={selected?.from}
                        onSelect={range => {
                            if (range?.from && range.to) {
                                setPending(undefined);
                                onChange({ from: parisDayStart(range.from), to: parisDayEnd(range.to) });
                                close();
                                return;
                            }
                            setPending(range);
                        }}
                    />
                </div>
            )}
        </FilterChip>
    );
}

function NumberRangeChip({ label, icon, hint, value, disabled, onChange }: { label: string; icon: LucideIcon; hint: string; value: Bounds; disabled?: boolean; onChange: (next: Bounds) => void }) {
    const [from, setFrom] = useState(value.from ?? "");
    const [to, setTo] = useState(value.to ?? "");

    useEffect(() => {
        setFrom(value.from ?? "");
        setTo(value.to ?? "");
    }, [value.from, value.to]);

    const summary = value.from && value.to ? `${value.from}–${value.to}` : value.from ? `≥ ${value.from}` : value.to ? `≤ ${value.to}` : null;

    return (
        <FilterChip label={label} icon={icon} summary={summary} disabled={disabled} onClear={() => onChange({})}>
            {close => (
                <form
                    className="space-y-2"
                    onSubmit={event => {
                        event.preventDefault();
                        onChange({ from: from.trim() || undefined, to: to.trim() || undefined });
                        close();
                    }}
                >
                    <p className="text-xs text-gray-500">{hint}</p>
                    <div className="flex items-center gap-2">
                        <Input type="number" min={0} value={from} onChange={event => setFrom(event.target.value)} placeholder="Min" aria-label={`${label} minimum`} className="h-8 w-24" />
                        <span className="text-xs text-gray-500">to</span>
                        <Input type="number" min={0} value={to} onChange={event => setTo(event.target.value)} placeholder="Max" aria-label={`${label} maximum`} className="h-8 w-24" />
                    </div>
                    <Button type="submit" size="sm" className="h-7 w-full text-xs">
                        Apply
                    </Button>
                </form>
            )}
        </FilterChip>
    );
}

function StateChip({ value, disabled, onChange }: { value: string | undefined; disabled?: boolean; onChange: (next: string | undefined) => void }) {
    const [pending, setPending] = useState(() => selectedOptionIds(parseStateFilter(value)));

    useEffect(() => {
        setPending(selectedOptionIds(parseStateFilter(value)));
    }, [value]);

    const toggle = (id: string) => {
        setPending(current => (current.includes(id) ? current.filter(item => item !== id) : [...current, id]));
    };

    return (
        <FilterChip label="State" icon={CircleDot} summary={stateSummary(value)} disabled={disabled} onClear={() => onChange(undefined)}>
            {close => (
                <form
                    className="space-y-2"
                    onSubmit={event => {
                        event.preventDefault();
                        const states = statesFromOptionIds(pending);
                        onChange(states.length ? states.join(",") : undefined);
                        close();
                    }}
                >
                    <p className="text-xs text-gray-500">Engine state of the card, not the last disposition shown in the table.</p>
                    <div className="space-y-1.5">
                        {STATE_OPTIONS.map(option => (
                            <label key={option.id} className="flex cursor-pointer items-center gap-2 text-sm">
                                <input
                                    type="checkbox"
                                    className="h-3.5 w-3.5 accent-primary"
                                    checked={pending.includes(option.id)}
                                    onChange={() => toggle(option.id)}
                                    aria-label={`State ${option.label}`}
                                />
                                {option.label}
                            </label>
                        ))}
                    </div>
                    <Button type="submit" size="sm" className="h-7 w-full text-xs">
                        Apply
                    </Button>
                </form>
            )}
        </FilterChip>
    );
}

export function CampaignCardsPanel({ campaignId, initial }: { campaignId: string | number; initial: CampaignCardsContent }) {
    const [content, setContent] = useState(initial);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [expanded, setExpanded] = useState(() => new Set(content.groups.map(group => group.status)));
    const [filters, setFilters] = useState<CampaignCardFilters>({});
    const filtered = Object.values(filters).some(Boolean);

    const load = async (nextFilters: CampaignCardFilters = filters) => {
        setLoading(true);
        setError(null);
        setFilters(nextFilters);
        try {
            const query = new URLSearchParams(Object.entries(nextFilters).filter(([, bound]) => bound) as [string, string][]).toString();
            const next = (await DataService.client
                .url(`/crm/objects/dialer-campaigns/${campaignId}/cards/${query ? `?${query}` : ""}`)
                .get()
                .json()) as CampaignCardsContent;
            setContent(next);
            setExpanded(new Set(next.groups.map(group => group.status)));
        } catch {
            setError("Campaign cards could not be loaded.");
        } finally {
            setLoading(false);
        }
    };

    const toggle = (status: string) => {
        setExpanded(current => {
            const next = new Set(current);
            if (next.has(status)) next.delete(status);
            else next.add(status);
            return next;
        });
    };

    return (
        <div aria-busy={loading}>
            <Section
                icon="Layers"
                title="Campaign cards"
                actions={
                    <Button size="sm" variant="outline" className="h-7 bg-white" onClick={() => load()} disabled={loading}>
                        <RefreshCw className="mr-1 h-4 w-4" /> Refresh
                    </Button>
                }
            >
                {error && (
                    <Alert variant="destructive">
                        <AlertCircle className="h-4 w-4" />
                        <AlertTitle>Cards unavailable</AlertTitle>
                        <AlertDescription className="flex flex-wrap items-center justify-between gap-2">
                            <span>{error}</span>
                            <Button size="sm" variant="outline" onClick={() => load()}>
                                <RefreshCw className="mr-1 h-4 w-4" /> Retry
                            </Button>
                        </AlertDescription>
                    </Alert>
                )}
                <p className="text-xs text-gray-500">
                    {formatNumber(content.total)} production cards{filtered ? " matching the filters" : ""}, grouped by status and disposition.
                </p>
                <div className="flex flex-wrap items-center gap-2">
                    <DayRangeChip
                        label="Created at"
                        icon={CalendarPlus}
                        hint="Cards enrolled between two days (Europe/Paris)."
                        disabled={loading}
                        value={{ from: filters.created_after, to: filters.created_before }}
                        onChange={bounds => load({ ...filters, created_after: bounds.from, created_before: bounds.to })}
                    />
                    <DayRangeChip
                        label="Last call"
                        icon={PhoneCall}
                        hint="Cards last called between two days (Europe/Paris). Never-called cards are excluded."
                        disabled={loading}
                        value={{ from: filters.last_call_after, to: filters.last_call_before }}
                        onChange={bounds => load({ ...filters, last_call_after: bounds.from, last_call_before: bounds.to })}
                    />
                    <NumberRangeChip
                        label="Priority"
                        icon={ArrowDownUp}
                        hint="Dispatch priority; lower is called first."
                        disabled={loading}
                        value={{ from: filters.priority_min, to: filters.priority_max }}
                        onChange={bounds => load({ ...filters, priority_min: bounds.from, priority_max: bounds.to })}
                    />
                    <NumberRangeChip
                        label="Attempts"
                        icon={Repeat}
                        hint="Calls placed at the card — the number of tries, not the steps crossed."
                        disabled={loading}
                        value={{ from: filters.attempts_min, to: filters.attempts_max }}
                        onChange={bounds => load({ ...filters, attempts_min: bounds.from, attempts_max: bounds.to })}
                    />
                    <StateChip value={filters.state} disabled={loading} onChange={state => load({ ...filters, state })} />
                    {filtered && (
                        <Button variant="outline" size="sm" className="h-7 bg-white" onClick={() => load({})} disabled={loading}>
                            <RotateCcw className="mr-1 h-4 w-4" /> Reset
                        </Button>
                    )}
                </div>
                {content.groups.length === 0 ? (
                    <StateBlock message={filtered ? "No cards match these filters." : "No cards in this campaign."} />
                ) : (
                    <Table>
                        <TableHeader className={denseTable.header}>
                            <TableRow className={denseTable.headerRow}>
                                <TableHead className={denseTable.head}>Status</TableHead>
                                <TableHead className={cn(denseTable.head, "text-right")}>Quantity</TableHead>
                                <TableHead className={cn(denseTable.head, "text-right")}>%</TableHead>
                                <TableHead className={cn(denseTable.head, "text-right")}>Avg. priority</TableHead>
                                <TableHead className={cn(denseTable.head, "text-right")}>Min. priority</TableHead>
                                <TableHead className={cn(denseTable.head, "text-right")}>Max. priority</TableHead>
                                <TableHead className={cn(denseTable.head, "text-right")}>Avg. attempts</TableHead>
                                <TableHead className={cn(denseTable.head, "text-right")}>Min. attempts</TableHead>
                                <TableHead className={cn(denseTable.head, "text-right")}>Max. attempts</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {content.groups.map(group => (
                                <Fragment key={group.status}>
                                    <TableRow className="bg-gray-50">
                                        <TableCell className={denseTable.cell}>
                                            <button
                                                type="button"
                                                className="flex items-center gap-1 rounded-sm text-left text-black hover:underline"
                                                onClick={() => toggle(group.status)}
                                                aria-expanded={expanded.has(group.status)}
                                            >
                                                {expanded.has(group.status) ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                                                {group.label}
                                            </button>
                                        </TableCell>
                                        <CardBreakdownCells row={group} />
                                    </TableRow>
                                    {expanded.has(group.status) &&
                                        group.dispositions.map((disposition, index) => (
                                            <TableRow key={`${group.status}-${disposition.name}-${index}`}>
                                                <TableCell className={cn(denseTable.cell, "pl-8")}>{disposition.name}</TableCell>
                                                <CardBreakdownCells row={disposition} />
                                            </TableRow>
                                        ))}
                                </Fragment>
                            ))}
                        </TableBody>
                    </Table>
                )}
            </Section>
        </div>
    );
}
